import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Flex, Heading, Spacer } from '@chakra-ui/react'
import { MdDeleteOutline, MdOutlineCheckCircle } from 'react-icons/md'
import { toast } from 'react-toastify'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

//Helper

import { RawTextCell } from '@/components/Table/components/Cell'
import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'
import { useSLA } from '../useSLA'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
const SLAListPage = () => {
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:SLA`, {})
  const query = convertTableStateToQuery(storedTableState)

  const { useFetchAllSLAs, useRemoveSLA, useUpdateSLA } = useSLA()
  const { data: SLAResponse } = useFetchAllSLAs(query)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const columnHelper = createColumnHelper()
  const removeSLA = useRemoveSLA()
  const updateSLA = useUpdateSLA()

  const handleAdd = () => {
    navigate('/admin/SLAs/create')
  }

  const handleDelete = useCallback(
    async (id) => {
      if (!id) {
        toast.error('Missing Id to remove Category')
      }
      try {
        dispatch(showLoadingOverlay())

        await removeSLA.mutateAsync(id)
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, removeSLA]
  )

  const handleActivate = useCallback(
    async (id) => {
      if (!id) {
        toast.error('Missing Id to activate Category')
      }
      try {
        dispatch(showLoadingOverlay())

        await updateSLA.mutateAsync({ id, data: { isDeleted: false } })
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, updateSLA]
  )

  const handleOnRowClick = (row) => {
    const SLA = row?.original
    if (!SLA?._id) {
      return
    }
    navigate(`/admin/SLAs/${SLA._id}`)
  }

  const defaultColumns = useMemo(
    () => [
      columnHelper.display({
        id: 'action',
        size: 100,
        enableHiding: false,
        enableColumnFilter: false,
        cell: ({ row: { original } }) => {
          return (
            <>
              {original?.status === 'active' && (
                <Flex _hover={{ cursor: 'pointer' }} color="gray.400" justifyContent={'center'} w="100%">
                  <MdDeleteOutline
                    size={24}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(original._id)
                    }}
                  />
                </Flex>
              )}
              {original?.status === 'inactive' && (
                <Flex _hover={{ cursor: 'pointer' }} color="green.400" justifyContent={'center'} w="100%">
                  <MdOutlineCheckCircle
                    size={24}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleActivate(original._id)
                    }}
                  />
                </Flex>
              )}
            </>
          )
        },
      }),

      columnHelper.accessor('templateName', {
        cell: RawTextCell,
      }),

      columnHelper.accessor('status', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: [
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
          ],
        },
      }),
    ],
    [columnHelper, handleActivate, handleDelete]
  )

  return (
    <>
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading> SLA Template</Heading>
          <Spacer />
          <Button colorScheme="blue" onClick={handleAdd}>
            Add SLA
          </Button>
        </Flex>
        <Box p={5} overflowX="scroll">
          <FullTable
            data={SLAResponse?.data ? SLAResponse.data : []}
            rowCount={SLAResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleOnRowClick}
          />
        </Box>
      </Box>
    </>
  )
}

export default SLAListPage
